import {
  Edit2,
  Eye,
  Plus,
  Trash2,
  Upload,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as CostIcon } from "../../assets/icons/bill.svg";
import AppButton from "../../components/ui/AppButton";
import DashboardFilterBar, {
  ASSIGNMENT_FILTER_OPTIONS,
  boolFromToggleFilter,
  filterSelectClass,
  isAllFilter,
  STATUS_FILTER_OPTIONS,
  stringFromFilter,
  TRIP_STATUS_FILTER_OPTIONS,
} from "../../components/ui/dashboard/DashboardFilterBar";
import Pagination from "../../components/ui/dashboard/Pagination";
import {
  ActiveStatusBadge,
  CurrentLocation,
} from "../../components/ui/dashboard/StatusCommon";
import Table, { Column } from "../../components/ui/dashboard/Table";
import ThreeDotsMenu from "../../components/ui/ThreeDotsMenu";
import { cn } from "../../lib/utils";
import { usePermissions } from "../../hooks/usePermissions";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import UserCard from "../user/ui/UserCard";
import { Vehicle, VehicleSearchRequest, VehicleSearchResponse } from "./@types";
import DeleteVehicleModal from "./components/DeleteVehicleModal";
import VehicleBulkUploadModal from "./components/VehicleBulkUploadModal";
import DashboardStats from "./refactor/VehicleDashStats";
import VehicleTypesDropdown from "./ui/VehicleTypesDropdown";
import { useGetVehiclesMutation } from "./vehicleApiSlice";
import VehicleModal from "./VehicleModal";

const VehiclesDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { canView, canCreate, canUpdate, canDelete } = usePermissions();
  const [searchTerm, setSearchTerm] = useState("");
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [assignedFilter, setAssignedFilter] = useState("all");
  const [tripStatusFilter, setTripStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [openModal, setOpenModal] = useState("");
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);

  const debouncedKeyword = useDebouncedValue(searchTerm.trim());

  const [getVehicles, { data: vehicleData, isLoading, error: loadError }] =
    useGetVehiclesMutation();

  const fetchVehicles = useCallback(async () => {
    const requestBody: VehicleSearchRequest = {
      pageNumber: currentPage,
      pageSize,
      keyword: debouncedKeyword || undefined,
      vehicleTypes: stringFromFilter(vehicleTypeFilter),
      tripStatus: stringFromFilter(tripStatusFilter),
      isAssigned: boolFromToggleFilter(
        assignedFilter,
        "assigned",
        "unassigned",
      ),
      isActive: boolFromToggleFilter(statusFilter, "active", "inactive"),
    };

    try {
      await getVehicles(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch vehicles:", err);
    }
  }, [
    currentPage,
    pageSize,
    debouncedKeyword,
    vehicleTypeFilter,
    statusFilter,
    assignedFilter,
    tripStatusFilter,
    getVehicles,
  ]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedKeyword, vehicleTypeFilter, statusFilter, assignedFilter, tripStatusFilter]);

  const handleFilterChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (value: string) => {
      setter(value || "all");
    };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setVehicleTypeFilter("all");
    setStatusFilter("all");
    setAssignedFilter("all");
    setTripStatusFilter("all");
  };

  const activeFilters = useMemo(() => {
    const chips: { key: string; label: string; onRemove: () => void }[] = [];

    if (debouncedKeyword) {
      chips.push({
        key: "keyword",
        label: `Search: ${debouncedKeyword}`,
        onRemove: handleClearSearch,
      });
    }
    if (!isAllFilter(vehicleTypeFilter)) {
      chips.push({
        key: "type",
        label: `Type: ${vehicleTypeFilter}`,
        onRemove: () => setVehicleTypeFilter("all"),
      });
    }
    if (!isAllFilter(statusFilter)) {
      const statusLabel =
        STATUS_FILTER_OPTIONS.find((option) => option.value === statusFilter)
          ?.label || statusFilter;
      chips.push({
        key: "status",
        label: `Status: ${statusLabel}`,
        onRemove: () => setStatusFilter("all"),
      });
    }
    if (!isAllFilter(assignedFilter)) {
      const assignedLabel =
        ASSIGNMENT_FILTER_OPTIONS.find(
          (option) => option.value === assignedFilter,
        )?.label || assignedFilter;
      chips.push({
        key: "assigned",
        label: `Assignment: ${assignedLabel}`,
        onRemove: () => setAssignedFilter("all"),
      });
    }
    if (!isAllFilter(tripStatusFilter)) {
      const tripLabel =
        TRIP_STATUS_FILTER_OPTIONS.find(
          (option) => option.value === tripStatusFilter,
        )?.label || tripStatusFilter;
      chips.push({
        key: "trip",
        label: `Trip: ${tripLabel}`,
        onRemove: () => setTripStatusFilter("all"),
      });
    }

    return chips;
  }, [
    debouncedKeyword,
    vehicleTypeFilter,
    statusFilter,
    assignedFilter,
    tripStatusFilter,
  ]);

  // Table columns configuration
  const columns: Column<Vehicle>[] = [
    {
      key: "vehicleFormatedId",
      header: "Vehicle ID",
      width: "20",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "vehicleName",
      header: "Vehicle name/Model",
      width: "24",
      render: (value: string, item) => (
        <span className="text-hColor capitalize">
          {value + " " + item.vehicleModel}
        </span>
      ),
    },
    {
      key: "vehicleType",
      header: "Name",
      width: "20",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "currentDriver",
      header: "Assigned driver",
      width: "20",
      render: (value: string, item) => (
        <UserCard fullName={item.currentDriver} imageUrl={item.driverImg} />
      ),
    },
    {
      key: "tripStatus",
      header: "Current location",
      width: "16",
      render: (value: string) => <CurrentLocation location={value} />,
    },
    {
      key: "isActive",
      header: "Status",
      width: "20",
      render: (value: boolean) => <ActiveStatusBadge isActive={value} />,
    },
    {
      key: "actions",
      header: "Action",
      width: "12",
      align: "center" as const,
      render: (value: any, item: Vehicle) => {
        return (
          <ThreeDotsMenu
            actions={[
              ...(canView("Dispatch")
                ? [
                    {
                      label: "Dispatch",
                      onClick: () => navigate(`/admin/dispatch/${item?.id}`),
                      icon: <CostIcon className="w-4 h-4" />,
                    },
                  ]
                : []),
              {
                label: "View vehicle",
                onClick: () => navigate(`/admin/vehicles/${item?.id}`),
                icon: <Eye className="w-4 h-4" />,
              },
              ...(canUpdate("Vehicles")
                ? [
                    {
                      label: "Edit",
                      onClick: () => handleUpdateAsset(item),
                      icon: <Edit2 className="w-4 h-4" />,
                    },
                  ]
                : []),
              ...(canDelete("Vehicles")
                ? [
                    {
                      label: "Delete vehicle",
                      onClick: () => {
                        setSelectedVehicle(item);
                        setOpenModal("delete");
                      },
                      icon: <Trash2 className="w-4 h-4" />,
                    },
                  ]
                : []),
            ]}
          />
        );
      },
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const handleUpdateAsset = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setOpenModal("edit");
  };

  const response = vehicleData?.data as VehicleSearchResponse | undefined;

  return (
    <div className="min-h-screen py-6 w-full bg-[#F9F9F9] p-4">
      <div className="w-full mx-auto space-y-6">
        {/* Dashboard Cards */}
        <div className="">
          <DashboardStats />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-[10px] p-4">
          <div className="">
            <h2 className="text-xl font-semibold text-hColor">All vehicles</h2>
          </div>

          {canCreate("Vehicles") && (
            <div className="flex gap-3">
              <AppButton
                appearance="outline"
                onClick={() => setOpenModal("bulk")}
                className="flex items-center px-4 py-2 rounded-lg w-auto h-[38px]"
              >
                <Upload className="h-4 w-4 mr-2" />
                Bulk upload
              </AppButton>
              <AppButton
                onClick={() => setOpenModal("add")}
                className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/60 transition-colors min-w-[146px] h-[38px]"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add vehicle
              </AppButton>
            </div>
          )}
        </div>

        {/* All vehicles section */}
        <div className="space-y-4 bg-white rounded-[10px] p-4">
          <DashboardFilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onClearSearch={handleClearSearch}
            onSearchSubmit={() => setSearchTerm(searchTerm.trim())}
            searchPlaceholder="Search by name, plate, ID or driver"
            isLoading={isLoading}
            extraFilters={
              <VehicleTypesDropdown
                showLabel={false}
                includeAll
                showSearch
                value={vehicleTypeFilter}
                onChange={handleFilterChange(setVehicleTypeFilter)}
                placeholder="Vehicle type"
                containerClassName="w-auto"
                className={cn(filterSelectClass, "w-full sm:w-[160px]")}
              />
            }
            filters={[
              {
                id: "status",
                placeholder: "Status",
                value: statusFilter,
                options: STATUS_FILTER_OPTIONS,
                onChange: handleFilterChange(setStatusFilter),
              },
              {
                id: "assignment",
                placeholder: "Assignment",
                value: assignedFilter,
                options: ASSIGNMENT_FILTER_OPTIONS,
                onChange: handleFilterChange(setAssignedFilter),
              },
              {
                id: "trip-status",
                placeholder: "Trip status",
                value: tripStatusFilter,
                options: TRIP_STATUS_FILTER_OPTIONS,
                onChange: handleFilterChange(setTripStatusFilter),
              },
            ]}
            chips={activeFilters}
            onClearAll={handleClearFilters}
            resultCount={response?.totalCount}
            resultLabel={response?.totalCount === 1 ? "vehicle" : "vehicles"}
          />

          <div className="overflow-hidden space-y-6">
            <Table
              data={response?.data || []}
              columns={columns}
              loading={isLoading}
              error={loadError}
              emptyMessage={
                activeFilters.length > 0
                  ? "No vehicles match your search or filters"
                  : "No vehicles found"
              }
            />
          </div>
        </div>
        <div className="">
          {/* Pagination */}
          {response && (
            <Pagination
              currentPage={response.currentPage}
              totalPages={response.totalPages}
              pageSize={response.pageSize}
              totalCount={response.totalCount}
              hasNextPage={response.hasNextPage}
              hasPreviousPage={response.hasPreviousPage}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </div>
      </div>
      <VehicleModal
        isOpen={openModal === "add" || openModal === "edit"}
        onClose={() => {
          fetchVehicles();
          setSelectedVehicle(null);
          setOpenModal("");
        }}
        vehicle={selectedVehicle}
      />

      <VehicleBulkUploadModal
        isOpen={openModal === "bulk"}
        onClose={() => {
          fetchVehicles();
          setOpenModal("");
        }}
      />

      {selectedVehicle && (
        <DeleteVehicleModal
          isOpen={openModal === "delete"}
          onClose={() => {
            fetchVehicles();
            setSelectedVehicle(null);
            setOpenModal("");
          }}
          vehicle={selectedVehicle}
        />
      )}
    </div>
  );
};

export default VehiclesDashboard;
